import { Component } from "@theme/component";
import { fetchConfig, debounce, resetLoading } from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #timeout = 5000;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];
       window.startCartItemLoader(lineItemRow?.dataset.key, 'Updating…');
  
   setTimeout(() => {
      if (typeof window.handleGiftProduct === 'function') {
        fetch('/cart.js')
          .then((res) => res.json())
          .then(async (cartState) => {
            await window.handleGiftProduct(cartState);

            const updatedCart = await fetch('/cart.js').then(res => res.json());

            document.dispatchEvent(
              new CustomEvent(ThemeEvents.cartUpdate, {
                detail: { data: { itemCount: updatedCart.item_count, isIncremental: false } }
              })
            );

            await sectionRenderer.renderSection(this.sectionId, { cache: false });
            window.stopCartItemLoader();   
          })
          .catch((err) => {
            console.error('Error fetching cart for gift product logic during cart update:', err);
            window.stopCartItemLoader();   
          });
      } else {
        window.stopCartItemLoader();   
      }
    }, 2500);

    if (!lineItemRow) return;

    // const removeButtons = /** @type {TextComponent | undefined} */ (
    //   lineItemRow.querySelectorAll(".cart-items__remove-button")
    // );
    // removeButtons?.forEach((button) => {
    //   button?.classList.add("btn--loading");
    // });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event) {
    event?.preventDefault();

    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
   window.startCartItemLoader(cartItemRowToRemove?.dataset.key, 'Removing…');  
   
   setTimeout(() => {
      if (typeof window.handleGiftProduct === 'function') {
        fetch('/cart.js')
          .then((res) => res.json())
          .then(async (cartState) => {
            await window.handleGiftProduct(cartState);

            const updatedCart = await fetch('/cart.js').then(res => res.json());

            document.dispatchEvent(
              new CustomEvent(ThemeEvents.cartUpdate, {
                detail: { data: { itemCount: updatedCart.item_count, isIncremental: false } }
              })
            );

            await sectionRenderer.renderSection(this.sectionId, { cache: false });
            window.stopCartItemLoader();  
          })
          .catch((err) => {
            console.error('Error fetching cart for gift product logic during cart update:', err);
            window.stopCartItemLoader();   
          });
      } else {
        window.stopCartItemLoader();   
      }
    }, 2500);
    if (!cartItemRowToRemove) return;

    // const removeButtons = /** @type {TextComponent | undefined} */ (
    //   cartItemRowToRemove.querySelectorAll(".cart-items__remove-button")
    // );
    // removeButtons.forEach((button) => {
    //   button?.classList.add("btn--loading");
    // });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.#disableCartItems();

    const { line, quantity } = config;

    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    fetch(`${FoxTheme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetLoading(this);

        // Even with errors, backend may have updated cart to max available
        // Update UI and cart count if we have sections
        if (parsedResponseText.sections && parsedResponseText.sections[this.sectionId]) {
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            "text/html"
          );

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

          this.dispatchEvent(
            new CartUpdateEvent({}, this.sectionId, {
              itemCount: newCartItemCount,
              source: "cart-items-component",
              sections: parsedResponseText.sections,
            })
          );

          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        } else if (parsedResponseText.errors) {
          // No sections in error response - fetch cart.js for accurate count and quantity

          const cartSectionsData = {};
          let cartJson = null;
          const cartSectionsPromises = Array.from(sectionsToUpdate).map(async (sectionId) => {
            const sectionUrl = `${window.location.pathname.split("?")[0]}?section_id=${sectionId}`;

            const res = await fetch(sectionUrl);
            const html = await res.text();

            cartSectionsData[sectionId] = html;
          });

          const cartJsonPromises = fetch(FoxTheme.routes.cart)
            .then((res) => res.json())
            .then((data) => {
              cartJson = data;
            });

          await Promise.all([...cartSectionsPromises, cartJsonPromises]);

          cartJson["sections"] = cartSectionsData;

          this.dispatchEvent(
            new CartUpdateEvent(cartJson, "", {
              itemCount: cartJson.item_count || 0,
              sections: cartJson.sections,
            })
          );

          morphSection(this.sectionId, cartJson.sections[this.sectionId]);
        }

        /**
         * Show error message if exists (e.g. quantity exceeds available)
         * Call after morph section to avoid message disappear
         */
        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        // cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    if (event?.detail?.sourceId === this.sectionId) return;
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");

    setTimeout(() => {
      cartItemErrorContainer.classList.add("hidden");
    }, this.#timeout);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event?.detail?.sourceId === this.sectionId) return;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}

(function injectCartImgLoaderStyles() {
  if (document.getElementById('cart-img-loader-styles')) return;
  const style = document.createElement('style');
  style.id = 'cart-img-loader-styles';
  style.textContent = `
    .cart-img-loading__overlay{
      position:absolute; inset:0; z-index:2;
      display:flex; align-items:center; justify-content:center;
      background:rgba(255,255,255,.55); border-radius:inherit;
      pointer-events:none;
    }
    .cart-img-loading__spinner{
      width:24px; height:24px; border-radius:50%;
      border:2px solid rgba(0,0,0,.2); border-top-color:#111;
      animation:cartImgSpin .7s linear infinite;
    }
    @keyframes cartImgSpin{ to{ transform:rotate(360deg); } }
  `;
  document.head.appendChild(style);
})();

function findCartItemImageBox(row) {
  if (!row) return null;
  return row.querySelector('.cart-items__media-container')
      || row.querySelector('.cart-items__media');
}

function showItemImageLoader(row, message) {
  const box = findCartItemImageBox(row);
  if (!box) return;
  if (box.querySelector('.cart-img-loading__overlay')) return; // already laga hai

  if (getComputedStyle(box).position === 'static') box.style.position = 'relative';

  const overlay = document.createElement('span');
  overlay.className = 'cart-img-loading__overlay';
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-label', message || 'Updating');
  overlay.innerHTML = '<span class="cart-img-loading__spinner" aria-hidden="true"></span>';
  box.appendChild(overlay);
}

function hideItemImageLoader() {
  document.querySelectorAll('.cart-img-loading__overlay').forEach((el) => el.remove());
}

let cartLoaderKey = null;
let cartLoaderMsg = '';
let cartLoaderSafety = null;


function applyLoader() {
  document.querySelectorAll('.cart-items__table-row').forEach((row) => {
    const box = findCartItemImageBox(row);
    if (!box) return;
    const isTarget = cartLoaderKey && row.dataset.key === cartLoaderKey;
    const existing = box.querySelector('.cart-img-loading__overlay');
    if (isTarget && !existing) {
      showItemImageLoader(row, cartLoaderMsg);
    } else if (!isTarget && existing) {
      existing.remove();
    }
  });
}


window.startCartItemLoader = function (key, message) {
  if (!key) return;
  cartLoaderKey = key;
  cartLoaderMsg = message || 'Updating…';
  applyLoader();
  clearTimeout(cartLoaderSafety);
  cartLoaderSafety = setTimeout(() => window.stopCartItemLoader(), 10000);
};


window.setGiftLoaderTarget = function (giftKey, message) {
  if (!giftKey) return;
  cartLoaderKey = giftKey;
  cartLoaderMsg = message || 'Removing free gift…';
  applyLoader();
  clearTimeout(cartLoaderSafety);
  cartLoaderSafety = setTimeout(() => window.stopCartItemLoader(), 10000);
};

window.stopCartItemLoader = function () {
  clearTimeout(cartLoaderSafety);
  cartLoaderKey = null;
  hideItemImageLoader();
};


document.addEventListener(ThemeEvents.cartUpdate, () => {
  if (!cartLoaderKey) return;
  setTimeout(applyLoader, 60);
});

const freeGiftWrapper = document.querySelector('.free-gift-wrapper');
const GIFT_VARIANT_ID = parseInt(freeGiftWrapper?.getAttribute('data-free-gift-id'));
const GIFT_THRESHOLD = freeGiftWrapper?.getAttribute('data-free-gift-threshold') * (Shopify.currency.rate);
const GIFT_THRESHOLD_QUANTITY = parseInt(freeGiftWrapper?.getAttribute('data-quantity-threshold'));
window.isGiftProductUpdating = false;

window.handleGiftProduct = async function (cartState) {

  if (window.isGiftProductUpdating) return;

  
  let freeGiftRemoved = sessionStorage.getItem("giftRemoved");
  if (window.giftRemovedManually || freeGiftRemoved == 'true') return;

  window.isGiftProductUpdating = true;

  try {
    if (!cartState || !cartState.items) return releaseGiftLock();
   
    let totalPrice = 0;
    let giftLineIndexes = [];
     let giftLineKeys = [];   
    let giftCount = 0;

    cartState.items.forEach((item, idx) => {
      if (
        parseInt(item.variant_id) === GIFT_VARIANT_ID &&
        item.properties && item.properties._gift === 'true'
      ) {
        giftLineIndexes.push(idx + 1);
        giftLineKeys.push(item.key);
        giftCount += item.quantity;
      } else if (parseInt(item.variant_id) !== GIFT_VARIANT_ID || !item.properties || item.properties._gift !== 'true') {
        if (item.product_type != "Device" && item.product_type != "Combo") {
          totalPrice += (item.final_line_price || (item.final_line_price * item.quantity)) / 100;
        }
      }
    });

  
    if (GIFT_THRESHOLD > 0) {
      if (totalPrice >= GIFT_THRESHOLD) {
        if (giftCount === 0) {
          await window.addGiftProductAsync(GIFT_VARIANT_ID);
        } else if (giftCount > 1) {
          await window.removeGiftProductsAsync(giftLineIndexes);
          await window.addGiftProductAsync(GIFT_VARIANT_ID);
        }
      } else if (giftLineIndexes.length > 0) {

         window.setGiftLoaderTarget(giftLineKeys[0], 'Removing free gift…'); 
        await window.removeGiftProductsAsync(giftLineIndexes);
      }
    }

   
    let cartQuantity = cartState.item_count
    if (giftCount > 0) {
      cartQuantity = cartQuantity - giftCount
    }
    if (GIFT_THRESHOLD_QUANTITY > 0) {
      if (cartQuantity >= GIFT_THRESHOLD_QUANTITY) {
        if (giftCount === 0) {
          await window.addGiftProductAsync(GIFT_VARIANT_ID);
        } else if (giftCount > 1) {
          await window.removeGiftProductsAsync(giftLineIndexes);
          await window.addGiftProductAsync(GIFT_VARIANT_ID);
        }
      } else if (giftLineIndexes.length > 0) {
        await window.removeGiftProductsAsync(giftLineIndexes);
      }
    }

  } finally {
    releaseGiftLock();
  }
};


function releaseGiftLock() {
  window.isGiftProductUpdating = false;
}

window.addGiftProductAsync = async function (variantId, d_gift = false) {

  const res = await fetch('/cart.js');
  const cart = await res.json();

  const autoGiftExists = cart.items.some(
    i => i.properties?._gift === 'true' && i.properties?._auto === 'true'
  );

  if (autoGiftExists) return;

 
  let freeGiftRemoved = sessionStorage.getItem("giftRemoved");
  if (window.giftRemovedManually || freeGiftRemoved == 'true') return;

  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: { _gift: 'true', _auto: 'true', _d_gift: d_gift }
    })
  });


};

window.removeGiftProductsAsync = async function () {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();

    const updates = {};

    // Loop through cart items
    cart.items.forEach(item => {
      // Only auto-added gifts
      if (item.properties?._gift === 'true' && item.properties?._auto === 'true') {
        if (item.key) {
          updates[item.key] = 0; // Shopify requires key here
        } else {
          console.warn('Gift item missing key:', item);
        }
      }
    });

   
    if (!Object.keys(updates).length) return;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });

  } catch (e) {
    console.error('removeGiftProductsAsync error:', e);
  }
};

window.syncAutoProductWithCart = function () {
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {

      window.handleGiftProduct(cart);
    });
};

document.addEventListener('DOMContentLoaded', function () {
  window.syncAutoProductWithCart();
});